import {
  ClipboardList,
  CreditCard,
  FolderKanban,
  Home,
  Inbox,
  KeyRound,
  LayoutDashboard,
  Mail,
  Shield,
  Users,
  type LucideIcon,
} from "lucide-react";

export type DirectoryAppId =
  | "dashboard"
  | "people"
  | "requests"
  | "groups"
  | "security"
  | "billing";

export type DirectoryModule = {
  href: string;
  label: string;
  icon: LucideIcon;
  /** Hidden unless the signed-in person can manage users. */
  admin?: boolean;
  match: (path: string, search: string) => boolean;
};

export type DirectoryApp = {
  id: DirectoryAppId;
  label: string;
  href: string;
  icon: LucideIcon;
  /** Hidden from the app bar unless the signed-in person can manage users. */
  admin?: boolean;
  match: (path: string) => boolean;
  modules: DirectoryModule[];
};

/**
 * Apps sit in the top bar. Modules for the open app sit in the left rail.
 * Home, People, Requests, Groups, Security, Billing.
 */
export const DIRECTORY_APPS: DirectoryApp[] = [
  {
    id: "dashboard",
    label: "Dashboard",
    href: "/",
    icon: LayoutDashboard,
    match: (path) => path === "/" || path.startsWith("/access"),
    modules: [
      { href: "/", label: "Overview", icon: Home, match: (path) => path === "/" },
      {
        href: "/access",
        label: "My access",
        icon: KeyRound,
        match: (path) => path.startsWith("/access"),
      },
    ],
  },
  {
    id: "people",
    label: "People",
    href: "/people",
    icon: Users,
    match: (path) => path.startsWith("/people"),
    modules: [
      {
        href: "/people",
        label: "People",
        icon: Users,
        match: (path, search) =>
          (path === "/people" || path.startsWith("/people/")) && !search.includes("invite=invite_pending"),
      },
      {
        href: "/people?invite=invite_pending",
        label: "Invites",
        icon: Mail,
        admin: true,
        match: (path, search) => path === "/people" && search.includes("invite=invite_pending"),
      },
    ],
  },
  {
    id: "requests",
    label: "Requests",
    href: "/requests",
    icon: Inbox,
    admin: true,
    match: (path) => path.startsWith("/requests"),
    modules: [
      {
        href: "/requests",
        label: "Pending",
        icon: Inbox,
        match: (path, search) =>
          path.startsWith("/requests") && !search.includes("status=all"),
      },
      {
        href: "/requests?status=all",
        label: "All",
        icon: ClipboardList,
        match: (path, search) => path.startsWith("/requests") && search.includes("status=all"),
      },
    ],
  },
  {
    id: "groups",
    label: "Groups",
    href: "/groups",
    icon: FolderKanban,
    admin: true,
    match: (path) => path.startsWith("/groups"),
    modules: [
      {
        href: "/groups",
        label: "All groups",
        icon: FolderKanban,
        match: (path) => path.startsWith("/groups"),
      },
    ],
  },
  {
    id: "security",
    label: "Security",
    href: "/security",
    icon: Shield,
    admin: true,
    match: (path) => path.startsWith("/security"),
    modules: [
      {
        href: "/security",
        label: "Policy",
        icon: Shield,
        match: (path, search) =>
          path.startsWith("/security") && !search.includes("tab=audit"),
      },
      {
        href: "/security?tab=audit",
        label: "Audit",
        icon: ClipboardList,
        match: (path, search) => path.startsWith("/security") && search.includes("tab=audit"),
      },
    ],
  },
  {
    id: "billing",
    label: "Billing",
    href: "/billing",
    icon: CreditCard,
    admin: true,
    match: (path) => path.startsWith("/billing"),
    modules: [
      {
        href: "/billing",
        label: "Overview",
        icon: CreditCard,
        match: (path, search) =>
          path.startsWith("/billing") && !search.includes("tab=audit"),
      },
      {
        href: "/billing?tab=audit",
        label: "Audit",
        icon: ClipboardList,
        match: (path, search) => path.startsWith("/billing") && search.includes("tab=audit"),
      },
    ],
  },
];

export function activeDirectoryApp(pathname: string): DirectoryApp {
  return DIRECTORY_APPS.find((app) => app.match(pathname)) ?? DIRECTORY_APPS[0]!;
}

/** Always show every Directory app so the module set stays discoverable; pages enforce rights. */
export function visibleDirectoryApps(_canManage: boolean): DirectoryApp[] {
  return DIRECTORY_APPS;
}

/** Always show every module for the open app; pages still enforce manage rights. */
export function visibleDirectoryModules(app: DirectoryApp, _canManage: boolean): DirectoryModule[] {
  return app.modules;
}
